const { useState, useEffect } = React;

// ─── Mobile-Hook ─────────────────────────────────────────────────────────
export function useIsMobile() {
  const [mob, setMob] = React.useState(window.innerWidth < 640);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

// ─── PWA-Install-Hook ────────────────────────────────────────────────────
export function useInstallPrompt() {
  const isStandalone = (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true;
  const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !isStandalone;
  const [canInstall, setCanInstall] = React.useState(!!(window.__obscuraInstall && window.__obscuraInstall.event));
  React.useEffect(() => {
    const on  = () => setCanInstall(!!(window.__obscuraInstall && window.__obscuraInstall.event));
    const off = () => setCanInstall(false);
    window.addEventListener("obscura:installable", on);
    window.addEventListener("obscura:installed", off);
    return () => {
      window.removeEventListener("obscura:installable", on);
      window.removeEventListener("obscura:installed", off);
    };
  }, []);
  const promptInstall = async () => {
    const e = window.__obscuraInstall && window.__obscuraInstall.event;
    if (!e) return;
    e.prompt();
    try { await e.userChoice; } catch (_) {}
    window.__obscuraInstall.event = null;
    setCanInstall(false);
  };
  return { canInstall: canInstall && !isStandalone, isIOS, promptInstall };
}

// ─── Focus-Trap fuer Modals (A11y) ────────────────────────────────────────
// Setzt den Fokus beim Oeffnen ins Modal, haelt Tab darin gefangen und gibt
// den Fokus beim Schliessen ans ausloesende Element zurueck.
export function useFocusTrap(active = true) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;
    const prev = document.activeElement;
    const SEL = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const list = () => Array.from(node.querySelectorAll(SEL)).filter(el => el.offsetParent !== null);
    if (!node.contains(document.activeElement)) (list()[0] || node).focus();
    function onKey(e) {
      if (e.key !== "Tab") return;
      const f = list();
      if (!f.length) { e.preventDefault(); node.focus(); return; }
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    node.addEventListener("keydown", onKey);
    return () => {
      node.removeEventListener("keydown", onKey);
      if (prev && !node.contains(prev) && typeof prev.focus === "function") prev.focus();
    };
  }, [active]);
  return ref;
}

// ─── Persistenz ──────────────────────────────────────────────────────────
export const STORAGE_PREFIX = "obscura.v1.";
export function usePersistentState(key, initial) {
  const fullKey = STORAGE_PREFIX + key;
  const [v, setV] = useState(() => {
    try {
      const raw = localStorage.getItem(fullKey);
      if (raw === null) return initial;
      return JSON.parse(raw);
    } catch (e) { return initial; }
  });
  useEffect(() => {
    try { localStorage.setItem(fullKey, JSON.stringify(v)); } catch (e) {}
  }, [fullKey, v]);
  return [v, setV];
}
